import React from "react";
import { JobAlerts, UserLocation } from "@themuse/design-system";
import { triggerSnowplowImpressionEvent, triggerSnowplowModalActionEvent } from "@themuse/snowplow-js-client";

import { jobAlertsSubscribeRequest, JobAlertsSubmissionValues } from "utils/api";
import { SingleJob } from "interfaces/api/apiInterfaces";
import { useInView } from "react-intersection-observer";

interface JobAlertsModuleProps {
    job: SingleJob;
    jobAlertViewed: boolean;
    setJobAlertViewed: React.Dispatch<React.SetStateAction<boolean>>;
    successViewed: boolean;
    setSuccessViewed: React.Dispatch<React.SetStateAction<boolean>>;
}

const getCategory = (job: SingleJob) => ({
    value: job.categories?.[0]?.code,
    label: job.categories?.[0]?.value,
});

const transformJobLocations = (locations) => locations?.filter((x) => x?.value)
    .map((location) => ({ value: location.value, label: location.value }));

const getUserLocation = (setUserLocation: { (value: React.SetStateAction<UserLocation>): void; (arg0: any): void; }) => {
    try {
        setUserLocation(JSON.parse(window.sessionStorage.getItem("user_location")));
    } catch (error) {
        console.error("Error parsing user location:", error);
    }
};

const getApiData = (values, job, isMultipleLocations, jobLocations, jobCategory): JobAlertsSubmissionValues => ({
    jobId: job.id,
    location: isMultipleLocations ? values?.location : jobLocations?.[0]?.value,
    category: jobCategory.value,
    level: job.levels?.[0]?.value,
    keyword: job.normalizedTitle,
    email: values?.email,
    newsletter: values?.newsletter,
    page_section: "job_detail"
});

const getTrackingData = (values, jobCategory, jobLocations, errorType) => ({
    sp_modal_type: "job_alert",
    sp_modal_error_type: errorType,
    sp_modal_action_type: "submit",
    sp_modal_screen_order: null,
    sp_modal_screen_name: null,
    sp_modal_newsletter_opt_in: values?.newsletter,
    sp_modal_job_category: jobCategory.value,
    sp_modal_job_location: jobLocations?.length
        ? jobLocations?.[0].label
        : values?.location
});

const JobAlertsWrapper = (props: JobAlertsModuleProps) => {
    const {
        job, jobAlertViewed, setJobAlertViewed, successViewed, setSuccessViewed
    } = props;
    const [userLocation, setUserLocation] = React.useState<UserLocation>();
    const jobCategory = React.useMemo(() => getCategory(job), [job]);
    const jobLocations = React.useMemo(() => transformJobLocations(job.locations), [job.locations]);
    const isMultipleLocations = !!jobLocations?.length;

    const jobAlertInViewCallback = (inView: boolean, entry: IntersectionObserverEntry) => {
        if (inView && !jobAlertViewed) {
            setJobAlertViewed(true);
            triggerSnowplowImpressionEvent(entry.target as HTMLElement);
        }
    };

    const successInViewCallback = (inView: boolean, entry: IntersectionObserverEntry) => {
        if (inView && !successViewed) {
            setSuccessViewed(true);
            triggerSnowplowImpressionEvent(entry.target as HTMLElement);
        }
    };

    const { ref: successRef } = useInView({ triggerOnce: true, rootMargin: "0px", onChange: successInViewCallback });
    const { ref: formRef } = useInView({ triggerOnce: true, rootMargin: "0px", onChange: jobAlertInViewCallback });

    React.useEffect(() => {
        getUserLocation(setUserLocation);
    }, []);

    const defaultLocation = React.useMemo(() => {
        if (userLocation?.city && isMultipleLocations) {
            return jobLocations.find((location) => location?.value?.includes(userLocation.city));
        }
        return null;
    }, [userLocation, isMultipleLocations, jobLocations]);

    const jobAlertsProps = React.useMemo(() => ({
        handleFormSubmit: async (values: any) => {
            const apiData = getApiData(values, job, isMultipleLocations, jobLocations, jobCategory);
            const optinSuccess = await jobAlertsSubscribeRequest(apiData);
            const errorType = optinSuccess
                ? null
                : "Job Alerts subscription was unsuccessful.";
            const trackingData = getTrackingData(values, jobCategory, jobLocations, errorType);
            triggerSnowplowModalActionEvent(trackingData);
            return optinSuccess;
        },
        jobCategory: jobCategory.label,
        jobLocations,
        successRef,
        defaultLocation,
        formRef
    }), [jobCategory, jobLocations, successRef, defaultLocation, formRef, job, isMultipleLocations]);

    return <JobAlerts {...jobAlertsProps} />;
};

export { JobAlertsWrapper };
